<template>
  <v-row class="ma-0 pa-0" style="position: relative">
    <v-col cols="3" class="ma-0 pa-0" v-if="province_options.length > 1">
      <v-select
        style="position: absolute; right: 0; z-index: 99"
        :items="province_options"
        v-model="selected_province"
        @change="set_up_graph_options"
        dense
      >
      </v-select>
    </v-col>
    <v-col cols="12" v-if="loading" class="chart_style ma-0 pa-0">
      <div class="geomap-title">{{ chart_title }}</div>
      <v-row
        class="geomap-chart-body geomap-loader ma-0 pa-0"
      >
        <v-progress-circular
          color="black"
          size="100"
          indeterminate
        ></v-progress-circular>
      </v-row>
    </v-col>
    <v-col cols="12" v-else class="chart_style ma-0 pa-0">
      <div class="geomap-title">{{ chart_title }}</div>
      <v-chart
        :id="chart_id"
        :ref="chart_ref"
        class="geomap-chart-body"
        :option="option"
        autoresize
      ></v-chart>
    </v-col>
  </v-row>
</template>

<script>
import VChart, { THEME_KEY } from "vue-echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import {
  TooltipComponent,
  LegendComponent,
} from "echarts/components";
import * as echarts from "echarts";

const PROVINCE_FSA_STARTING_CODE_MAPPING = {
  'ontario': ['K', 'L', 'M', 'N', 'P'],
  'quebec': ['G', 'H', 'J'],
  'british_columbia': ['V'],
  'alberta': ['T'],
  'saskatchewan': ['S'],
  'manitoba': ['R'],
  'new_brunswick': ['E'],
  'nova_scotia': ['B'],
  'prince_edward_island': ['C'],
  'newfoundland_labrador': ['A'],
  'yukon': ['Y'],
  'northwest_territories': ['X'],
  'nunavut': ['X']
}
const delay = (ms) => new Promise((res) => setTimeout(res, ms));

use([CanvasRenderer, TooltipComponent, LegendComponent]);
export default {
  name: "GeoMap",
  props: ["chart_title", "chart_data", "chart_id", "chart_ref"],
  components: {
    VChart,
  },
  provide: {
    [THEME_KEY]: "light",
  },
  mounted() {
    this.set_up_graph_options();
  },
  data: () => ({
    option: {},
    loading: true,
    selected_province: "ontario",
    province_data: [],
    heatmap_colors_typical: [
      "#000003", "#000006", "#01010b", "#02020f", "#040415", "#060519", "#09071f", "#0c0926", "#0e0a2a", "#110c31",
      "#140d35", "#170f3c", "#1b1044", "#1e1049", "#221150", "#251155", "#2a115c", "#2d1060", "#321067", "#370f6c",
      "#3b0f6f", "#400f73", "#430f75", "#481078", "#4d117a", "#50127b", "#55137d", "#58157e", "#5d177e", "#60187f",
      "#651a80", "#691c80", "#6c1d80", "#711f81", "#742081", "#792281", "#7e2481", "#812581", "#852681", "#892881",
      "#8d2980", "#922b80", "#952c80", "#9a2d7f", "#9e2e7e", "#a3307e", "#a6317d", "#ab337c", "#b0347b", "#b3357a",
      "#b83778", "#bb3877", "#c03a75", "#c53c74", "#c83d72", "#cd3f70", "#d0416f", "#d4436d", "#d7456b", "#dc4869",
      "#e04b66", "#e24d65", "#e65162", "#e85461", "#ec585f", "#ee5d5d", "#f0605d", "#f3655c", "#f4685b", "#f66e5b",
      "#f7735c", "#f8775c", "#f97d5d", "#fa805e", "#fb8660", "#fb8a62", "#fc9064", "#fd9567", "#fd9969", "#fd9f6c",
      "#fda26f", "#fea873", "#feae76", "#feb179", "#feb77d", "#febb80", "#fec085", "#fec488", "#fec98d", "#fdcf92",
      "#fdd295", "#fdd89a", "#fddc9d", "#fde1a3", "#fce6a8", "#fceaac", "#fcf0b1", "#fcf3b5", "#fbf9bb", "#fbfcbf"
    ],
    heatmap_colors_red_blue: [
      "#0000ff", "#0200fc", "#0500f9", "#0700f7", "#0a00f4", "#0c00f2", "#0f00ef", "#1200ec", "#1400ea", "#1700e7",
      "#1900e5", "#1c00e2", "#1e00e0", "#2100dd", "#2400da", "#2600d8", "#2900d5", "#2b00d3", "#2e00d0", "#3100cd",
      "#3300cb", "#3600c8", "#3800c6", "#3b00c3", "#3e00c0", "#4000be", "#4300bb", "#4500b9", "#4800b6", "#4b00b3",
      "#4d00b1", "#5000ae", "#5200ac", "#5500a9", "#5800a6", "#5a00a4", "#5d00a1", "#5f009f", "#62009c", "#650099",
      "#670097", "#6a0094", "#6d0091", "#6f008f", "#72008c", "#74008a", "#770087", "#7a0084", "#7c0082", "#7f007f",
      "#82007c", "#84007a", "#870077", "#890075", "#8c0072", "#8f006f", "#91006d", "#94006a", "#960068", "#990065",
      "#9c0062", "#9e0060", "#a1005d", "#a4005a", "#a60058", "#a90055", "#ab0053", "#ae0050", "#b1004d", "#b3004b",
      "#b60048", "#b80046", "#bb0043", "#be0040", "#c0003e", "#c3003b", "#c50039", "#c80036", "#cb0033", "#cd0031",
      "#d0002e", "#d3002b", "#d50029", "#d80026", "#da0024", "#dd0021", "#e0001e", "#e2001c", "#e50019", "#e70017",
      "#ea0014", "#ec0012", "#ef000f", "#f2000c", "#f4000a", "#f70007", "#f90005", "#fc0002", "#ff0000"
    ],
    available_province_options: [
        { text: "Ontario", value: "ontario" },
        { text: "Quebec", value: "quebec" },
        { text: "British Columbia", value: "british_columbia" },
        { text: "Alberta", value: "alberta" },
        { text: "Saskatchewan", value: "saskatchewan" },
        { text: "Manitoba", value: "manitoba" },
        { text: "New Brunswick", value: "new_brunswick" },
        { text: "Nova Scotia", value: "nova_scotia" },
        { text: "Prince Edward Island", value: "prince_edward_island" },
        { text: "Newfoundland and Labrador", value: "newfoundland_labrador" },
        { text: "Yukon", value: "yukon" },
        { text: "Northwest Territories", value: "northwest_territories" },
        { text: "Nunavut", value: "nunavut" }
    ],
    province_options: [

    ]

  }),
  computed: {
  },
  methods: {
    async set_up_graph_options() {
      this.loading = true;

      const fsaFirstLetters = new Set(
        this.chart_data
          .filter(d => typeof d.name === 'string' && d.name.length > 0)
          .map(d => d.name[0].toUpperCase())
      );
      this.province_options =  this.available_province_options.filter(province => {
        const fsaPrefixes = PROVINCE_FSA_STARTING_CODE_MAPPING[province.value] || [];
        return fsaPrefixes.some(prefix => fsaFirstLetters.has(prefix));
      })

      if (!this.province_options.some(province => province.value === this.selected_province) && this.province_options.length > 0) {
        this.selected_province = this.province_options[0].value;
      }
      
      // this.selected_province = this.province_options[0]['value'];

      const fsa_file = `/data/GeoData/${this.selected_province}_fsa.json`;
      const json_output = await fetch(fsa_file)
        .then((res) => res.json())
        .then((json) => {
          return json;
        });

      echarts.registerMap("Canada", json_output);

      // Filter data based on selected province
      const starting_codes = PROVINCE_FSA_STARTING_CODE_MAPPING[this.selected_province];
      
      // const fsaFirstLetters = new Set(
      //   this.chart_data
      //     .filter(d => typeof d.name === 'string' && d.name.length > 0)
      //     .map(d => d.name[0].toUpperCase())
      // );

      // this.province_options =  this.available_province_options.filter(province => {
      //     const fsaPrefixes = PROVINCE_FSA_STARTING_CODE_MAPPING[province.value] || [];
      //     return fsaPrefixes.some(prefix => fsaFirstLetters.has(prefix));
      //   })

      this.province_data = this.chart_data.filter(item => {
        return starting_codes.includes(item.name.charAt(0).toUpperCase());
      });
      let maxValue = this.province_data.length > 0 ? Math.max(...this.province_data.map(item => item.value)) : 1;
      const option = {
        tooltip: {
          trigger: "item",
          showDelay: 0,
          transitionDuration: 0.2,
          formatter: function (params) {
            // If no value, show 0%
            let value = params.value 
            if(value > 0.01){
              value = value.toFixed(2) + '%'
            }
            else if(value > 0.00001){
              value = value.toFixed(5) + '%'
            }
            else if(value > 0){
              value = value + '%'
            }
            else{
              value = '0%';
            }
            // != null ? (params.value).toFixed(3) + '%' : '0%';
            return `${params.marker}${params.name}<br/>${value}`;
          }
        },
        visualMap: {
          left: "right",
          min: 0,
          max: maxValue,
          inRange: {
            color: this.heatmap_colors_red_blue,
          },
          text: ["High", "Low"],
          calculable: true,
        },
        series: [
          {
            name: "FSA Analysis",
            type: "map",
            roam: true,
            map: "Canada",
            renderMode: "canvas",
            top: 8,
            bottom: 24,
            zoom: 1.3,
            scaleLimit: {
              min: 0.75
            },
            emphasis: {
              label: {
                show: true,
              },
            },
            data: this.province_data,
          },
        ],
      };
      this.option = option;
      await delay(500);
      this.loading = false;
    },
  },
};
</script>

<style scoped>
.chart_title {
  font-family: "Poppins" !important;
  font-size: 10pt !important;
  line-height: 40px;

  display: block;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chart_style {
  height: 60vh;
  font-family: "Poppins" !important;
}
.geomap-title {
  height: 35px;
  /* line-height: 40px; */
  text-align: center;
  font-family: "Poppins" !important;
  font-size: 18px;
  font-weight: 600;
}
.geomap-chart-body {
  display: block;
  width: 100%;
  height: calc(100% - 40px);
}
.geomap-loader {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
